import React from 'react';
import { TextInput, TextInputField } from 'evergreen-ui';
import { isDifference } from './utils';

/**
 * TextInput или TextInputField, который изменяет данные только когда происходит onBlur.
 * Значение НЕ изменяется если инпут расфокусился с такими же значением каким и были.
 * ("7" и 7 будет считаться одинаковыми значениями)
 *
 * Выбор компонента (TextInput или TextInputField) зависит от наличия label или description
 *
 * Для обновления значения value использовать функцию updateValue
 * @param props
 * @prop {string | number | undefined} value
 * @prop {boolean} [resetInvalid=false] позволяет сбрасывать значение на то, что было перед введен НЕвалидным значением
 * @prop {(event, setInsideValue) => void} updateValue функция изменения значения, которая вызывается при onBlur.
 *                                     В нее возвращается event от onBlur и setValue,
 *                                    для изменения внутреннего значения в инпуте при необходимости
 * @prop ...TextInputProps
 */
const ThisTextInputBlur = React.forwardRef((props, ref) => {
  const {
    value: initialValue,
    resetInvalid,
    updateValue,
    label,
    description,
    ...inputProps
  } = props;

  const [value, setValue] = React.useState(initialValue);

  // на случай, если меняется входное значение без изменений в текущем инпуте
  React.useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);
  // console.log('%c ThisTextInputBlur', 'color: gold', { resetInvalid, name: inputProps.name, initialValue, value });

  const onChange = React.useCallback((e) => {
    setValue(e.target.value);
    if (inputProps.onChange) inputProps.onChange(e);
  }, [inputProps]);

  const onKeyPressEnterBlur = React.useCallback((e) => {
    if (e.key === 'Enter') e.target.blur();
    if (inputProps.onKeyPress) inputProps.onKeyPress(e);
  }, [inputProps]);

  /*
    Изменяем данные когда теряем фокус
    и при этом значение было изменено (отлично от начального)
    и валидно, если установлен флаг сбрасывания на предыдущее значение
    */
  const onBlur = React.useCallback((e) => {
    const { validity } = e.target;
    if (isDifference(initialValue, value) && (!resetInvalid || (resetInvalid && validity.valid))) {
      if (updateValue) updateValue(e, setValue);
    } else setValue(initialValue);

    if (inputProps.onBlur) inputProps.onBlur(e);
  }, [initialValue, inputProps, resetInvalid, updateValue, value]);

  if (label || description) {
    return (
      <TextInputField
        label={label || ''}
        description={description}
        ref={ref}
        value={value || ''}
        {...inputProps}
        onChange={onChange}
        onBlur={onBlur}
        onKeyPress={onKeyPressEnterBlur}
      />
    );
  }
  return (
    <TextInput
      ref={ref}
      value={value || ''}
      {...inputProps}
      onChange={onChange}
      onBlur={onBlur}
      onKeyPress={onKeyPressEnterBlur}
    />
  );
});
/**
 * Инпут, который изменяет данные только когда происходит onBlur.
 * Значение НЕ изменяется если инпут расфокусился с такими же значением каким и были.
 * ("7" и 7 будет считаться одинаковыми значениями)
 *
 * Для обновления значения value использовать функцию updateValue
 * @param value
 * @param {boolean} [resetInvalid=false] позволяет сбрасывать значение на то, что было перед введен НЕвалидным значением
 * @param {(event, setInsideValue) => void} updateValue функция изменения значения, которая вызывается при onBlur.
 *                                     В нее возвращается event от onBlur и setValue,
 *                                    для изменения внутреннего значения в инпуте при необходимости
 */
export const TextInputBlur = React.memo(ThisTextInputBlur);
TextInputBlur.displayName = 'TextInputBlur';
ThisTextInputBlur.displayName = 'ThisTextInputBlur';
